// (c) chaehongee. Confidential and proprietary.
import type { ReactElement } from 'react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import styles from '../../Canvas.module.css'
import { Eraser } from 'lucide-react'
import type { Rect, Size } from './types'

const DRAW_BRUSH_MIN = 4
const DRAW_BRUSH_MAX = 256

export interface DrawOverlayHandle {
    exportOverlayBlob: () => Promise<Blob | null>
    reset: () => void
    undo: () => void
    redo: () => void
    hasChanges: () => boolean
}

interface DrawOverlayProps {
    frameRect: Rect
    imageRect: Rect
    initialImageRect: Rect | null
    imageNaturalSize: Size | null
    contentScale: number
    brushSizeCssPx: number
    erasing: boolean
    onToggleEraser: () => void
    onBrushSizeChange: (nextCssPx: number) => void
}

/**
 * DrawOverlay renders a clipped drawing surface strictly within the given frameRect.
 * It maintains a high-res offscreen canvas aligned to the image's natural pixels,
 * and mirrors strokes onto the visible canvas. Export returns a PNG sized to the
 * natural frame size ready for compositing.
 */
export const DrawOverlay = forwardRef<DrawOverlayHandle, DrawOverlayProps>(function DrawOverlay(props, ref): ReactElement {
    const {
        frameRect,
        imageRect,
        initialImageRect,
        imageNaturalSize,
        contentScale,
        brushSizeCssPx,
        erasing,
        onToggleEraser,
        onBrushSizeChange,
    } = props

    const containerRef = useRef<HTMLDivElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)

    // Offscreen natural-resolution canvas
    const hiRef = useRef<HTMLCanvasElement | null>(null)

    const drawingRef = useRef(false)
    const lastPointRef = useRef<{ x: number; y: number } | null>(null)
    const dirtyRef = useRef(false)

    // Simple undo/redo stacks of ImageData for high-res buffer
    const undoStackRef = useRef<ImageData[]>([])
    const redoStackRef = useRef<ImageData[]>([])

    // Calculate dimensions EXACTLY as export does (exportCroppedImageWithOverlay)
    // Same order of operations to avoid any floating point differences
    const userScaleX = initialImageRect && initialImageRect.w !== 0 ? imageRect.w / initialImageRect.w : 1.0
    const userScaleY = initialImageRect && initialImageRect.h !== 0 ? imageRect.h / initialImageRect.h : 1.0

    const scaledNaturalW = imageNaturalSize ? imageNaturalSize.w * userScaleX : imageRect.w
    const scaledNaturalH = imageNaturalSize ? imageNaturalSize.h * userScaleY : imageRect.h

    // Frame ratio (same as export)
    const frameRatioW = imageRect.w !== 0 ? frameRect.w / imageRect.w : 1
    const frameRatioH = imageRect.h !== 0 ? frameRect.h / imageRect.h : 1

    // Final output dimensions (EXACT same calculation as export)
    const outW = Math.max(1, Math.round(scaledNaturalW * frameRatioW))
    const outH = Math.max(1, Math.round(scaledNaturalH * frameRatioH))

    // Calculate stroke scale from frame coordinates to output canvas coordinates
    // This maps from the visible frame rectangle to the final output dimensions
    const worldToExportScaleX = frameRect.w !== 0 ? outW / frameRect.w : 1
    const worldToExportScaleY = frameRect.h !== 0 ? outH / frameRect.h : 1

    const ensureCanvases = useCallback(() => {
        const dpr = window.devicePixelRatio || 1
        const cv = canvasRef.current
        if (cv) {
            const cssW = Math.max(1, frameRect.w)
            const cssH = Math.max(1, frameRect.h)
            const pxScale = dpr // contentScale is already applied via parent transform
            const w = Math.round(cssW * pxScale)
            const h = Math.round(cssH * pxScale)
            if (cv.width !== w || cv.height !== h) {
                cv.width = w
                cv.height = h
            }
        }
        if (imageNaturalSize) {
            const hi = hiRef.current || document.createElement('canvas')
            // Use EXACT same dimensions as export output
            if (hi.width !== outW || hi.height !== outH) {
                hi.width = outW
                hi.height = outH
                // Clear when resized
                const hictx = hi.getContext('2d')
                hictx?.clearRect(0, 0, outW, outH)
            }
            hiRef.current = hi
        }
    }, [frameRect.w, frameRect.h, imageNaturalSize, outW, outH, userScaleX, userScaleY])

    useEffect(() => {
        ensureCanvases()
    }, [ensureCanvases, contentScale])

    const drawSegment = useCallback((from: { x: number; y: number }, to: { x: number; y: number }) => {
        // Visible canvas in CSS pixels -> device pixels
        const dpr = window.devicePixelRatio || 1
        const v = canvasRef.current
        const vctx = v?.getContext('2d')
        if (v && vctx) {
            vctx.save()
            vctx.lineCap = 'round'
            vctx.lineJoin = 'round'
            vctx.strokeStyle = erasing ? 'rgba(0,0,0,1)' : 'rgba(255,0,0,1)'
            vctx.globalCompositeOperation = erasing ? 'destination-out' : 'source-over'
            vctx.lineWidth = Math.max(1, brushSizeCssPx) * dpr
            vctx.beginPath()
            vctx.moveTo(from.x * dpr, from.y * dpr)
            vctx.lineTo(to.x * dpr, to.y * dpr)
            vctx.stroke()
            vctx.restore()
        }
        // High-res natural canvas
        const hi = hiRef.current
        const hictx = hi?.getContext('2d')
        if (hi && hictx) {
            const scaleX = worldToExportScaleX
            const scaleY = worldToExportScaleY
            // Use average scale for brush size to maintain circular appearance
            const avgScale = (scaleX + scaleY) / 2
            hictx.save()
            hictx.lineCap = 'round'
            hictx.lineJoin = 'round'
            hictx.strokeStyle = erasing ? 'rgba(0,0,0,1)' : 'rgba(255,0,0,1)'
            hictx.globalCompositeOperation = erasing ? 'destination-out' : 'source-over'
            hictx.lineWidth = Math.max(1, Math.round(brushSizeCssPx * avgScale))
            hictx.beginPath()
            hictx.moveTo(from.x * scaleX, from.y * scaleY)
            hictx.lineTo(to.x * scaleX, to.y * scaleY)
            hictx.stroke()
            hictx.restore()
        }
        dirtyRef.current = true
    }, [brushSizeCssPx, erasing, worldToExportScaleX, worldToExportScaleY])

    const pushUndoSnapshot = useCallback(() => {
        const hi = hiRef.current
        if (!hi) return
        const hictx = hi.getContext('2d')
        if (!hictx) return
        try {
            const snap = hictx.getImageData(0, 0, hi.width, hi.height)
            undoStackRef.current.push(snap)
            // cap stack
            if (undoStackRef.current.length > 100) {
                undoStackRef.current.shift()
            }
            // clearing redo on new stroke
            redoStackRef.current = []
        } catch {
            // ignore
        }
    }, [])

    const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (!canvasRef.current) return
        pushUndoSnapshot()
        drawingRef.current = true
        const rect = (e.target as HTMLCanvasElement).getBoundingClientRect()
        // Map from screen px (after transforms) to local CSS px (pre-transform)
        const scaleFactor = rect.width / Math.max(1, frameRect.w)
        const x = (e.clientX - rect.left) / (scaleFactor || 1)
        const y = (e.clientY - rect.top) / (scaleFactor || 1)
        lastPointRef.current = { x, y }
    }, [pushUndoSnapshot, frameRect.w])

    const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawingRef.current) return
        const rect = (e.target as HTMLCanvasElement).getBoundingClientRect()
        const scaleFactor = rect.width / Math.max(1, frameRect.w)
        const x = (e.clientX - rect.left) / (scaleFactor || 1)
        const y = (e.clientY - rect.top) / (scaleFactor || 1)
        const last = lastPointRef.current
        if (last) {
            drawSegment(last, { x, y })
            lastPointRef.current = { x, y }
        } else {
            lastPointRef.current = { x, y }
        }
    }, [drawSegment, frameRect.w])

    const handlePointerUp = useCallback(() => {
        drawingRef.current = false
        lastPointRef.current = null
    }, [])

    const exportOverlayBlob = useCallback(async (): Promise<Blob | null> => {
        if (!hiRef.current || !dirtyRef.current) return null
        return await new Promise<Blob | null>((resolve) => {
            hiRef.current?.toBlob((b) => resolve(b), 'image/png')
        })
    }, [])

    const reset = useCallback(() => {
        // clear both canvases
        const v = canvasRef.current
        const c = v?.getContext('2d')
        if (v && c) {
            c.clearRect(0, 0, v.width, v.height)
        }
        const hi = hiRef.current
        const hictx = hi?.getContext('2d')
        if (hi && hictx) {
            hictx.clearRect(0, 0, hi.width, hi.height)
        }
        dirtyRef.current = false
        undoStackRef.current = []
        redoStackRef.current = []
    }, [])

    const undo = useCallback(() => {
        const hi = hiRef.current
        const hictx = hi?.getContext('2d')
        if (!hi || !hictx) return
        const snap = undoStackRef.current.pop()
        if (!snap) return
        try {
            // Push current
            const cur = hictx.getImageData(0, 0, hi.width, hi.height)
            redoStackRef.current.push(cur)
            if (redoStackRef.current.length > 100) {
                redoStackRef.current.shift()
            }
        } catch {
            // ignore
        }
        hictx.putImageData(snap, 0, 0)
        // sync visible
        const v = canvasRef.current
        const vctx = v?.getContext('2d')
        if (v && vctx) {
            vctx.clearRect(0, 0, v.width, v.height)
            // draw scaled down snapshot for preview fidelity
            vctx.drawImage(hi, 0, 0, v.width, v.height)
        }
    }, [])

    const redo = useCallback(() => {
        const hi = hiRef.current
        const hictx = hi?.getContext('2d')
        if (!hi || !hictx) return
        const snap = redoStackRef.current.pop()
        if (!snap) return
        try {
            const cur = hictx.getImageData(0, 0, hi.width, hi.height)
            undoStackRef.current.push(cur)
        } catch {
            // ignore
        }
        hictx.putImageData(snap, 0, 0)
        const v = canvasRef.current
        const vctx = v?.getContext('2d')
        if (v && vctx) {
            vctx.clearRect(0, 0, v.width, v.height)
            vctx.drawImage(hi, 0, 0, v.width, v.height)
        }
    }, [])

    const hasChanges = useCallback(() => {
        return undoStackRef.current.length > 0
    }, [])

    useImperativeHandle(ref, () => ({
        exportOverlayBlob,
        reset,
        undo,
        redo,
        hasChanges,
    }), [exportOverlayBlob, reset, undo, redo, hasChanges])

    return (
        <div
            ref={containerRef}
            className={styles.drawOverlay}
            style={{ left: frameRect.x, top: frameRect.y, width: frameRect.w, height: frameRect.h }}
            onContextMenu={(e) => e.preventDefault()}
        >
            <canvas
                ref={canvasRef}
                className={styles.drawCanvas}
                style={{ width: frameRect.w, height: frameRect.h }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
            />
            <div
                className={styles.drawControls}
                data-canvas-ui="true"
                style={{ transform: `scale(${1 / (contentScale || 1)})`, transformOrigin: 'right top' }}
            >
                <button
                    className={styles.toolbarButton + (erasing ? ' ' + styles.drawButtonActive : '')}
                    title="Eraser"
                    onClick={(e) => { e.stopPropagation(); onToggleEraser() }}
                    aria-label="Eraser"
                >
                    <Eraser className={styles.icon} aria-hidden="true" />
                </button>
                <label className={styles.toolbarText} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    size:
                    <input
                        type="range"
                        min={DRAW_BRUSH_MIN}
                        max={DRAW_BRUSH_MAX}
                        step={1}
                        value={Math.max(DRAW_BRUSH_MIN, Math.min(DRAW_BRUSH_MAX, Math.round(brushSizeCssPx)))}
                        className="slider"
                        onChange={(e) => onBrushSizeChange(Number(e.currentTarget.value))}
                    />
                </label>
            </div>
        </div>
    )
})

